<!-- ----------------------------------------- template ----------------------------------------- -->
<template>
  <div>
    <div class="achievement-intro" style="margin-bottom: 24px; padding: 16px; background-color: #f5f7fa; border-radius: 4px;">
      <h3 style="margin: 0 0 12px 0; color: #303133;">Internal Achievements</h3>
      <p style="margin: 0; color: #606266; line-height: 1.6;">
        This module is designed for managing and implementing achievements <strong>internally within your club</strong>. Club advisors and leadership can use this section to design recognition systems and award internal achievements to club members for their contributions, participation, leadership, or other accomplishments. These are club-managed recognitions that help celebrate member success and motivate continued engagement within your organization.
      </p>
    </div>
    <div class="flex justify-end">
      <el-button type="primary" size="small" @click="openNewAchievementDialog">
        + New Achievement
      </el-button>
    </div>

    <empty-state
      v-if="!achievementList || achievementList.length === 0"
      message="No achievements yet"
      description="Click 'New Achievement' to celebrate your club's first accomplishment!"
      style="margin-top: 20px"
    ></empty-state>

    <el-table
      v-else
      :data="achievementList"
      style="margin-top: 20px"
      :highlight-current-row="false"
      :highlight-selection-row="false"
      :header-cell-class-name="'custom-head-cell'"
      :row-class-name="'custom-row-bg'"
    >
      <el-table-column prop="title" label="Achievement">
      </el-table-column>
      <el-table-column prop="date" label="Date">
        <template slot-scope="scope">
          {{ formatDate(scope.row.date) }}
        </template>
      </el-table-column>
      <el-table-column prop="type" label="Type">
        <template slot-scope="scope">
          <el-tag :type="getTypeTag(scope.row.type)">{{ scope.row.type.charAt(0).toUpperCase() + scope.row.type.slice(1) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Operation" width="200px">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="handleEdit(scope.row)">
            Edit
          </el-button>
          <el-button 
            type="danger" 
            size="small" 
            @click="handleDelete(scope.row)"
          >
            Delete
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Achievement Dialog -->
    <el-dialog
      :title="isEditing ? 'Edit Achievement' : 'New Achievement'"
      :visible.sync="dialogVisible"
      width="50%"
    >
      <el-form :model="achievementForm" :rules="rules" ref="achievementForm" label-width="100px">
        <el-form-item label="Title" prop="title">
          <el-input v-model="achievementForm.title"></el-input>
        </el-form-item>
        
        <el-form-item label="Date" prop="date">
          <el-date-picker
            v-model="achievementForm.date"
            type="date"
            placeholder="Select date"
            style="width: 100%"
          >
          </el-date-picker>
        </el-form-item>

        <el-form-item label="Type" prop="type">
          <el-select v-model="achievementForm.type" placeholder="Select type" style="width: 100%">
            <el-option label="Award" value="award"></el-option>
            <el-option label="Competition" value="competition"></el-option>
            <el-option label="Recognition" value="recognition"></el-option>
            <el-option label="Other" value="other"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="Description" prop="description">
          <el-input
            type="textarea"
            :rows="4"
            v-model="achievementForm.description"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="submitAchievement">
          {{ isEditing ? 'Save' : 'Create' }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<!-- ------------------------------------------ script ------------------------------------------ -->
<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType } from '@/utils/store'
import EmptyState from '@/components/EmptyState.vue'

export default {
  name: 'Achievements',
  components: {
    EmptyState
  },
  props: {
    clubInfo: {
      type: Object,
      default: () => ({})
    },
    club_id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      achievementList: [],
      dialogVisible: false,
      isEditing: false,
      currentUserId: getUserId(),
      achievementForm: {
        title: '',
        date: '',
        type: '',
        description: ''
      },
      rules: {
        title: [
          { required: true, message: 'Please enter title', trigger: 'blur' },
          { min: 3, max: 100, message: 'Length should be 3 to 100 characters', trigger: 'blur' }
        ],
        date: [
          { required: true, message: 'Please select date', trigger: 'change' }
        ],
        type: [
          { required: true, message: 'Please select type', trigger: 'change' }
        ],
        description: [
          { required: true, message: 'Please enter description', trigger: 'blur' }
        ]
      }
    }
  },

  mounted() {
    this.fetchAchievements()
  },

  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    getTypeTag(type) {
      const types = {
        'award': 'success',
        'competition': 'primary',
        'recognition': 'warning',
        'other': 'info'
      }
      return types[type] || 'info'
    },

    openNewAchievementDialog() {
      this.isEditing = false
      this.achievementForm = {
        title: '',
        date: '',
        type: '',
        description: ''
      }
      this.dialogVisible = true
    },

    handleEdit(achievement) {
      this.isEditing = true
      this.achievementForm = { ...achievement }
      this.dialogVisible = true
    },

    async handleDelete(achievement) {
      try {
        await this.$confirm('Are you sure you want to delete this achievement?', 'Warning', {
          type: 'warning'
        })
        
        const response = await postRequest('/club/deleteAchievement', {
          achievement_id: achievement._id,
          club_id: this.club_id
        })
        
        if (response.code === 0) {
          this.$message.success('Achievement deleted successfully')
          this.fetchAchievements()
        } else {
          throw new Error(response.msg)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deleting achievement:', error)
          this.$message.error(error.message || 'Failed to delete achievement')
        }
      }
    },

    async submitAchievement() {

        await this.$refs.achievementForm.validate()
        const response = await postRequest('updateAchievement', {
          id: this.isEditing ? this.achievementForm._id.$id : '',
          club_id: this.club_id,
          user_id: this.currentUserId,
          user_type: getUserType(),
          title: this.achievementForm.title,
          date: this.achievementForm.date,
          type: this.achievementForm.type,
          description: this.achievementForm.description
        })
        
        if (response.data.code === 0) {
          this.$message.success(`Achievement ${this.isEditing ? 'updated' : 'created'} successfully`)
          this.dialogVisible = false
          this.fetchAchievements()
        } else {
          throw new Error(response.msg)
        }

    },

    async fetchAchievements() {

        const response = await postRequest('getAchievements', {
          club_id: this.club_id
        })
        if (response.data.code === 0) {
          this.achievementList = response.data.data
        } else {
          throw new Error(response.data.msg)
        }

    }
  }
}
</script>

<!-- ------------------------------------------ style ------------------------------------------ -->
<style scoped lang="scss">
.custom-head-cell {
  background: #FF8833;
  color: white;
  box-sizing: border-box;
  font-family: GravityBold;
}

:deep(.el-table) {
  .el-button--text {
    padding: 0;
    margin-right: 16px;
  }
  
  .el-button--danger {
    padding: 7px 15px;
    margin-right: 16px;
    background-color: white;
    border: 1px solid #F56C6C;
    color: #F56C6C;

    &:hover {
      background-color: #F56C6C;
      color: white;
    }
  }
}
</style> 